import { useState, useRef, useEffect, ImgHTMLAttributes } from "react";

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  fallbackSrc?: string;
  alt: string;
  eager?: boolean;
}

const OptimizedImage = ({ src, fallbackSrc, alt, eager = false, className, ...props }: OptimizedImageProps) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Derive JPG fallback from WebP path if not provided
  const jpgFallback = fallbackSrc || src.replace(/\.webp$/, ".jpg");
  const isWebP = src.endsWith(".webp");

  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  const handleError = () => {
    if (isWebP && imgSrc === src) {
      setImgSrc(jpgFallback);
    }
  };

  return (
    <img
      ref={imgRef}
      src={imgSrc}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding={eager ? "sync" : "async"}
      fetchPriority={eager ? "high" : undefined}
      onError={handleError}
      onLoad={() => setIsLoaded(true)}
      className={`${className || ""} transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
      {...props}
    />
  );
};

export default OptimizedImage;
